import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { Reveal, SectionHeading } from "@/components/Reveal";

const Node = ({ title, sub, align = "center", testid }) => (
  <div className={`flex flex-col ${align === "left" ? "items-start text-left" : align === "right" ? "items-end text-right" : "items-center text-center"}`} data-testid={testid}>
    <h3 className="font-serif text-xl sm:text-2xl lg:text-[1.7rem] font-medium text-slate-100 leading-snug whitespace-pre-line">{title}</h3>
    {sub}
  </div>
);

export default function BridgeSection() {
  const { t } = useLang();
  const ease = [0.16, 1, 0.3, 1];

  return (
    <section id="bridge" className="relative py-24 lg:py-32 bg-obsidian-surface overflow-hidden" data-testid="bridge-section">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading overline={t.bridge.overline} title={t.bridge.title} text={t.bridge.text} testid="bridge-title" />

        <div className="mt-16 lg:mt-24 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] items-center gap-14 lg:gap-6">
          <Reveal>
            <Node
              testid="bridge-node-africa"
              align="left"
              title={t.bridge.left}
              sub={<span className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-gold/80"><span className="w-8 h-px bg-gold/60" />{t.bridge.africaToWorld}</span>}
            />
          </Reveal>

          {/* Bridge diagram */}
          <div className="relative w-full max-w-[560px] mx-auto lg:w-[560px]" data-testid="bridge-diagram">
            <svg viewBox="0 0 560 240" fill="none" className="w-full h-auto">
              {/* piers */}
              {[120, 280, 440].map((x) => (
                <motion.line key={x} x1={x} y1={150} x2={x} y2={210} stroke="rgba(212,175,55,0.35)" strokeWidth="1.4"
                  initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.9, ease }} />
              ))}
              {/* main deck */}
              <motion.path d="M40 150 H520" stroke="#D4AF37" strokeWidth="2"
                initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, delay: 0.2, ease }} />
              {/* arches */}
              <motion.path d="M120 150 C 160 80, 240 80, 280 150" stroke="rgba(212,175,55,0.8)" strokeWidth="1.6" fill="none"
                initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.2, delay: 0.7, ease }} />
              <motion.path d="M280 150 C 320 80, 400 80, 440 150" stroke="rgba(212,175,55,0.8)" strokeWidth="1.6" fill="none"
                initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.2, delay: 1.0, ease }} />
              {/* suspenders */}
              {[150, 180, 210, 240, 320, 350, 380, 410].map((x, i) => {
                const arc = (cx) => {
                  const span = 160;
                  const tt = Math.abs(x - cx) / (span / 2);
                  return 150 - Math.sqrt(Math.max(0, 1 - tt * tt)) * 70;
                };
                const cx = x < 280 ? 200 : 360;
                return (
                  <motion.line key={x} x1={x} y1={arc(cx)} x2={x} y2={150} stroke="rgba(212,175,55,0.3)" strokeWidth="0.8"
                    initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 1.2 + i * 0.06, ease }} />
                );
              })}
              {/* endpoints */}
              <motion.circle cx="40" cy="150" r="5" fill="#0C1226" stroke="#D4AF37" strokeWidth="1.6"
                initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.2 }} />
              <motion.circle cx="520" cy="150" r="5" fill="#0C1226" stroke="#D4AF37" strokeWidth="1.6"
                initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 1.7 }} />
              {/* water shimmer */}
              {[0, 1, 2].map((i) => (
                <motion.line key={i} x1={90 + i * 130} y1={222} x2={170 + i * 130} y2={222} stroke="rgba(136,150,171,0.25)" strokeWidth="1"
                  initial={{ opacity: 0 }} whileInView={{ opacity: [0, 1, 0.3] }} viewport={{ once: true }} transition={{ duration: 2, delay: 1.6 + i * 0.2 }} />
              ))}
            </svg>
            {/* center emblem below the bridge */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 1.4, ease }}
              className="mt-6 flex justify-center"
            >
              <div className="px-6 py-2.5 bg-obsidian border border-gold/60 rounded-full backdrop-blur-md shadow-[0_0_40px_rgba(212,175,55,0.15)]">
                <span className="font-serif tracking-[0.18em] uppercase text-sm sm:text-base text-gold whitespace-nowrap" data-testid="bridge-node-center">{t.bridge.center}</span>
              </div>
            </motion.div>
          </div>

          <Reveal delay={0.15}>
            <div className="flex flex-col items-start lg:items-end" data-testid="bridge-node-world">
              <Node
                align="right"
                title={t.bridge.right}
                sub={<span className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-gold/80">{t.bridge.worldToAfrica}<span className="w-8 h-px bg-gold/60" /></span>}
              />
              <div className="mt-8 flex flex-wrap gap-2 lg:justify-end max-w-sm">
                {t.bridge.pills.map((p, i) => (
                  <motion.span
                    key={p}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 + i * 0.07 }}
                    data-testid={`bridge-pill-${i}`}
                    className="px-4 py-1.5 text-xs tracking-wide text-slate-300 border hairline rounded-full hover:border-gold/50 hover:text-gold transition-colors duration-300 cursor-default"
                  >
                    {p}
                  </motion.span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
